import {
  Columns,
  Container,
  Heading,
  Stack,
  Text,
  Visible,
} from '@spaceship-fspl/components';
import {
  fill as SFill,
  getColor,
  paddingY,
  text,
} from '@spaceship-fspl/styles';
import { TrackingCategory } from '@spaceship-fspl/tracking';
import { PanelSection } from 'components';
import React, { useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import worldMapSVG from './world-map.svg';

const MapImageContainer = styled.figure`
  background-image: url(${worldMapSVG});
  background-repeat: no-repeat;
  background-size: contain;
  min-height: 360px;
  margin: 0;
`;

const CountryPinCircle = styled.circle`
  ${SFill('pink.050')}
`;

const CountryInvestmentSizeCircle = styled.circle`
  ${SFill('pink.050')}
  opacity: 0.2;
  transition: 0.3s ease-in-out;
`;

const CountryGroup = styled.g`
  rect {
    opacity: 0;
  }

  &:hover ${CountryInvestmentSizeCircle} {
    transition: 0.4s cubic-bezier(0.67, -1.02, 0.41, 0.98);
    opacity: 0.3;
  }
`;

const SVG = styled.svg.attrs({
  xmlns: 'http://www.w3.org/2000/svg',
  viewBox: '0 0 906 427',
  width: '100%',
  height: '100%',
})<{ isCountryFocussed: boolean }>`
  overflow: visible;

  ${({ isCountryFocussed }) =>
    isCountryFocussed &&
    css`
      ${CountryGroup}:not(:hover) ${CountryInvestmentSizeCircle} {
        opacity: 0.1;
      }
    `}
`;

const MobileListItem = styled.li`
  display: flex;
  justify-content: space-between;
  border-top: 1px solid ${getColor('neutral.050')};
  ${paddingY('xs')}

  &:last-child {
    border-bottom: 1px solid ${getColor('neutral.050')};
  }
`;

const MobileList = styled.ul`
  margin: 0;
  padding: 0;
`;

interface CountryData {
  country: string;
  percentage: number;
  x: number;
  y: number;
}

export interface LocationsPanelProps {
  title: string;
  disclaimer: string;
  data: CountryData[];
}

interface FocussedCountryType {
  name: string;
  x: number;
  y: number;
  percentage: number;
}

const POINT_RADIUS = 1.8;
const POINT_X_TRANSLATE = -2.5;

const getInvestmentHoverRadius = (percentage: number): number =>
  Math.max(Math.min(POINT_RADIUS * percentage * 2.5, 40), 5);

export const LocationsPanel: React.FC<
  React.PropsWithChildren<LocationsPanelProps>
> = ({ data, title, disclaimer }) => {
  const [focussedCountry, setFocussedCountry] = useState<
    FocussedCountryType | undefined
  >(undefined);

  const handleHoverIn = ({ country, percentage, x, y }: CountryData): void => {
    setFocussedCountry({
      name: country,
      x: x + POINT_X_TRANSLATE,
      y: y - getInvestmentHoverRadius(percentage),
      percentage,
    });
  };

  const handleHoverOut = (): void => {
    setFocussedCountry(undefined);
  };

  return (
    <TrackingCategory category="Locations Panel">
      <PanelSection backgroundColor={{ md: 'indigo.100' }}>
        <Container>
          <Stack spaceY={{ xs: 'md', md: 'xl', lg: 'xxl' }}>
            <Heading
              variant={2}
              align="center"
              color={{ xs: 'indigo.100', md: 'neutral.000' }}
            >
              {title}
            </Heading>

            <Visible isHidden={{ xs: true, md: false }} displayValue="block">
              <MapImageContainer>
                <SVG isCountryFocussed={Boolean(focussedCountry)}>
                  <defs>
                    <filter
                      x="-12.9%"
                      y="-38.6%"
                      width="125.7%"
                      height="177.1%"
                      filterUnits="objectBoundingBox"
                      id="shadow"
                    >
                      <feOffset
                        dx="0"
                        dy="0"
                        in="SourceAlpha"
                        result="shadowOffsetOuter"
                      />
                      <feGaussianBlur
                        stdDeviation="7"
                        in="shadowOffsetOuter"
                        result="shadowBlurOuter"
                      />
                      <feColorMatrix
                        values="0 0 0 0 0   0 0 0 0 0   0 0 0 0 0  0 0 0 0.19287817 0"
                        type="matrix"
                        in="shadowBlurOuter"
                        result="shadowMatrixOuter"
                      />
                      <feMerge>
                        <feMergeNode in="shadowMatrixOuter" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>

                  {data.map((countryData, index) => {
                    const { country, percentage, x, y } = countryData;
                    const investmentRadius =
                      getInvestmentHoverRadius(percentage);
                    const investmentHoverRadius = Math.max(
                      Math.min(2 * investmentRadius, 60),
                      7,
                    );

                    return (
                      <CountryGroup
                        key={index}
                        onMouseOver={() => handleHoverIn(countryData)}
                        onMouseOut={handleHoverOut}
                        // a11y friendly
                        onFocus={() => handleHoverIn(countryData)}
                        onBlur={handleHoverOut}
                        transform={`translate(${POINT_X_TRANSLATE}, 0)`}
                      >
                        <rect
                          x={x - investmentRadius}
                          y={y - investmentRadius}
                          height={investmentRadius * 2}
                          width={investmentRadius * 2}
                        />
                        <CountryPinCircle r={POINT_RADIUS} cx={x} cy={y} />
                        <CountryInvestmentSizeCircle
                          cx={x}
                          cy={y}
                          r={
                            focussedCountry && focussedCountry.name === country
                              ? investmentHoverRadius
                              : investmentRadius
                          }
                        />
                      </CountryGroup>
                    );
                  })}

                  {focussedCountry && (
                    <FocussedCountryTooltip {...focussedCountry} />
                  )}
                </SVG>
              </MapImageContainer>
            </Visible>

            <Visible isHidden={{ md: true }} displayValue="block">
              <MobileList>
                {data
                  .sort((a, b) => b.percentage - a.percentage)
                  .map(({ country, percentage }) => (
                    <MobileListItem key={`${country}_mobile_list`}>
                      <Text variant={1} isBold={false}>
                        {country}
                      </Text>
                      <Text variant={1} isBold={true}>
                        {percentage}%
                      </Text>
                    </MobileListItem>
                  ))}
              </MobileList>
            </Visible>

            <Columns>
              <Columns.Column
                offset={{ xs: 0, lg: 1 / 12 }}
                width={{ xs: 1, lg: 10 / 12 }}
              >
                <Text
                  variant={3}
                  color={{ xs: 'neutral.085', md: 'neutral.070' }}
                  align={{ xs: 'left', md: 'center' }}
                >
                  {disclaimer}
                </Text>
              </Columns.Column>
            </Columns>
          </Stack>
        </Container>
      </PanelSection>
    </TrackingCategory>
  );
};

const TOOLTIP_HEIGHT = 46;
const TOOLTIP_WIDTH = 220;
const TOOLTIP_ARROW_HEIGHT = 10;
const TOOLTIP_ARROW_WIDTH = 20;
const TOOLTIP_BORDER_RADIUS = 5;
const TOOLTIP_PADDING_X = 16;
const TOOLTIP_PADDING_Y = 29;
const TOOLTIP_ARROW_POINT_AX = (TOOLTIP_WIDTH - TOOLTIP_ARROW_WIDTH) / 2;
const TOOLTIP_ARROW_POINT_AY = TOOLTIP_HEIGHT - 1;
const TOOLTIP_ARROW_POINT_BX = (TOOLTIP_WIDTH + TOOLTIP_ARROW_WIDTH) / 2;
const TOOLTIP_ARROW_POINT_BY = TOOLTIP_ARROW_POINT_AY;
const TOOLTIP_ARROW_POINT_CX = TOOLTIP_WIDTH / 2;
const TOOLTIP_ARROW_POINT_CY = TOOLTIP_ARROW_HEIGHT + TOOLTIP_HEIGHT - 1;

const fadeInAnimation = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const TooltipGroup = styled.g`
  animation: ${fadeInAnimation} 0.3s ease-in-out forwards;
`;

const TooltipRect = styled.rect`
  box-shadow: 0 0 14px 0 rgba(0, 0, 0, 0.19);
  fill: white;
`;

const TooltipCountryText = styled.text`
  ${text({ variant: 3 })}
`;

const TooltipPercentageText = styled.text`
  ${text({ variant: 2 })}
  font-weight: bold;
`;

const TooltipArrowPolygon = styled.polygon`
  fill: white;
`;

const FocussedCountryTooltip: React.FC<
  React.PropsWithChildren<FocussedCountryType>
> = ({ x, y, name, percentage }) => (
  <TooltipGroup
    transform={`translate(
      ${x - TOOLTIP_WIDTH / 2},
      ${y - TOOLTIP_HEIGHT - TOOLTIP_ARROW_HEIGHT}
    )`}
    filter="url(#shadow)"
  >
    <TooltipRect
      height={TOOLTIP_HEIGHT}
      width={TOOLTIP_WIDTH}
      rx={TOOLTIP_BORDER_RADIUS}
      ry={TOOLTIP_BORDER_RADIUS}
    />

    <TooltipCountryText x={TOOLTIP_PADDING_X} y={TOOLTIP_PADDING_Y}>
      {name}
    </TooltipCountryText>

    <TooltipPercentageText
      textAnchor="end"
      x={TOOLTIP_WIDTH - TOOLTIP_PADDING_X}
      y={TOOLTIP_PADDING_Y}
      dy={1}
    >
      {percentage}%
    </TooltipPercentageText>

    <TooltipArrowPolygon
      points={`
        ${TOOLTIP_ARROW_POINT_AX},${TOOLTIP_ARROW_POINT_AY}
        ${TOOLTIP_ARROW_POINT_BX},${TOOLTIP_ARROW_POINT_BY}
        ${TOOLTIP_ARROW_POINT_CX},${TOOLTIP_ARROW_POINT_CY}
      `}
    />
  </TooltipGroup>
);
